import {
  formatHint,
  resolvePostFormat,
  SocialPostFormatHint,
  SocialPostFormatName,
  SocialPostMedia,
} from 'fym-shared/src/music24/socialPostFormat';
import { postImageRefusal, ratioLabel } from 'fym-shared/src/music24/postImageRules';
import { byteLimitLabel, postVideoRefusal, secondsLimitLabel } from 'fym-shared/src/music24/postVideoRules';
import { platformMeta } from '../platformMeta';
import { ScheduledPostFormat, SocialPlatform } from '../types';

/** Why a platform cannot take the post (`resolvePostFormat` gives null, or a media rule refuses it). */
export type ComposerFormatRefusal =
  | 'noStories'
  | 'storyNeedsMedia'
  | 'needsVideo'
  | 'needsMedia'
  | 'imageAspect'
  | 'videoSize'
  | 'videoTooShort'
  | 'videoTooLong'
  | 'videoAspect';

export interface ComposerFormatLine {
  /** The format that the platform makes from the post. Null: the post cannot go out there. */
  format: SocialPostFormatName | null;
  /** Why a video goes out as a normal video and not as a Reel or a Short. */
  hint: SocialPostFormatHint | null;
  /** Why the post cannot go out. Set only when `format` is null. */
  refusal: ComposerFormatRefusal | null;
  /** The shape of the media ("3:1") when the refusal is `imageAspect` or `videoAspect`. */
  ratio?: string;
  /** The limit of the platform ("64 MB", "60 s") when the refusal is a video size or length. */
  limit?: string;
}

/** A video refusal of the Composer: the refusal and the words of its limit. */
export interface ComposerVideoRefusal {
  refusal: Extract<ComposerFormatRefusal, 'videoSize' | 'videoTooShort' | 'videoTooLong' | 'videoAspect'>;
  ratio?: string;
  limit?: string;
}

/**
 * The video rule of the platform (fym-shared `postVideoRules.ts`, the same rule as the server): the
 * size, the length and the shape of the attached video, as far as they are known. An uploaded video
 * is an MP4 or a MOV, and a kit video an MP4, so the type is not checked here.
 */
export function composerVideoRefusal(
  platform: SocialPlatform,
  media: SocialPostMedia | null,
  format: ScheduledPostFormat
): ComposerVideoRefusal | null {
  if (media === null || media.kind !== 'video') {
    return null;
  }
  const refusal = postVideoRefusal(platform, format, {
    mimeType: null,
    sizeBytes: media.sizeBytes ?? null,
    durationSeconds: media.durationSeconds,
    width: media.width,
    height: media.height,
  });
  switch (refusal?.reason) {
    case 'size':
      return { refusal: 'videoSize', limit: byteLimitLabel(refusal.maxBytes) };
    case 'tooShort':
      return { refusal: 'videoTooShort', limit: secondsLimitLabel(refusal.minSeconds) };
    case 'tooLong':
      return { refusal: 'videoTooLong', limit: secondsLimitLabel(refusal.maxSeconds) };
    case 'aspect':
      return { refusal: 'videoAspect', ratio: ratioLabel(refusal.ratio) };
    // The Composer sends only MP4 and MOV, so the type rule has no line of its own.
    case 'type':
    case undefined:
      return null;
  }
}

/**
 * The image rule of the platform (fym-shared `postImageRules.ts`, the same rule as the server).
 * The Composer knows the pixel size of the image, not its type or its bytes (an upload is a JPEG of
 * at most 8 MB, a kit image is a JPEG), so only the shape is checked here.
 */
export function composerImageRefusal(
  platform: SocialPlatform,
  media: SocialPostMedia | null,
  format: ScheduledPostFormat
): { ratio: string } | null {
  if (media === null || media.kind !== 'image') {
    return null;
  }
  const refusal = postImageRefusal(platform, format, {
    mimeType: null,
    sizeBytes: null,
    width: media.width,
    height: media.height,
  });
  return refusal?.reason === 'aspect' ? { ratio: ratioLabel(refusal.ratio) } : null;
}

function refusalFor(
  platform: SocialPlatform,
  media: SocialPostMedia | null,
  format: ScheduledPostFormat
): ComposerFormatRefusal {
  if (format === 'story') {
    return platformMeta[platform].supportsStories ? 'storyNeedsMedia' : 'noStories';
  }
  // YouTube takes a video only. A photo there is refused with the same words as no media.
  return platformMeta[platform].mediaNeed === 'video' || media !== null ? 'needsVideo' : 'needsMedia';
}

/**
 * The format line of one selected channel (design social-connectors e). The publishers use the
 * same `resolvePostFormat`, so the label and the post agree.
 */
export function composerFormatLine(
  platform: SocialPlatform,
  media: SocialPostMedia | null,
  format: ScheduledPostFormat
): ComposerFormatLine {
  const resolved = resolvePostFormat(platform, media, format);
  if (resolved === null) {
    return { format: null, hint: null, refusal: refusalFor(platform, media, format) };
  }
  const imageRefusal = composerImageRefusal(platform, media, format);
  if (imageRefusal !== null) {
    return { format: null, hint: null, refusal: 'imageAspect', ratio: imageRefusal.ratio };
  }
  const videoRefusal = composerVideoRefusal(platform, media, format);
  if (videoRefusal !== null) {
    return { format: null, hint: null, ...videoRefusal };
  }
  return { format: resolved, hint: formatHint(platform, media, format), refusal: null };
}
